import { useCallback, useEffect, useState } from "react";
import { api } from "@repo/api-client";
import type {
  CreateMoodEntryRequest,
  MoodEntry,
  MoodRecommendationsResponse,
} from "@repo/shared-types";

interface MoodDiscoveryState {
  history: MoodEntry[];
  recommendations: MoodRecommendationsResponse | null;
  loading: boolean;
  saving: boolean;
  error: string;
  refresh: () => Promise<void>;
  submitAssessment: (payload: CreateMoodEntryRequest) => Promise<void>;
}

export function useMoodDiscovery(): MoodDiscoveryState {
  const [history, setHistory] = useState<MoodEntry[]>([]);
  const [recommendations, setRecommendations] = useState<MoodRecommendationsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setError("");
    const data = await api.listMoodHistory(30);
    setHistory(data);
  }, []);

  useEffect(() => {
    refresh()
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Failed to load mood history");
      })
      .finally(() => setLoading(false));
  }, [refresh]);

  const submitAssessment = useCallback(async (payload: CreateMoodEntryRequest) => {
    setSaving(true);
    setError("");
    try {
      const entry = await api.saveMoodEntry(payload);
      const nextRecommendations = await api.getMoodRecommendations({
        ...payload,
        mood_entry_id: entry.id,
      });
      setRecommendations(nextRecommendations);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit mood assessment");
      throw err;
    } finally {
      setSaving(false);
    }
  }, [refresh]);

  return {
    history,
    recommendations,
    loading,
    saving,
    error,
    refresh,
    submitAssessment,
  };
}
